import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from './axios';
import type {
  DesktopArtifactUploadResponse,
  DesktopPluginAdminListQuery,
  DesktopPluginCreateRequest,
  DesktopPluginDownloadUrlRequest,
  DesktopPluginItem,
  DesktopPluginUpdateRequest,
  DesktopPluginUploadQuery,
  DesktopPluginUploadUrlRequest,
} from '@cms/models';
import { postArtifactMultipartUpload } from '@/lib/upload/multipart-api-upload';

export const desktopPluginsQueryKeys = {
  all: ['desktop-plugins'] as const,
  list: (filters?: DesktopPluginAdminListQuery) =>
    ['desktop-plugins', 'list', filters ?? {}] as const,
};

export const desktopPluginsApi = {
  async list(filters?: DesktopPluginAdminListQuery): Promise<DesktopPluginItem[]> {
    const { data } = await apiClient.get<DesktopPluginItem[]>('/desktop-plugins', {
      params: filters ?? undefined,
    });
    return Array.isArray(data) ? data : [];
  },

  async uploadUrl(payload: DesktopPluginUploadUrlRequest): Promise<{
    uploadUrl: string;
    objectKey: string;
    expiresInSec: number;
  }> {
    const { data } = await apiClient.post('/desktop-plugins/upload-url', payload);
    return data;
  },

  async uploadArtifact(
    query: DesktopPluginUploadQuery,
    file: File,
    onProgress?: (percent: number) => void,
  ): Promise<DesktopArtifactUploadResponse> {
    return postArtifactMultipartUpload<DesktopArtifactUploadResponse>({
      path: '/desktop-plugins/upload',
      file,
      query: {
        appSlug: query.appSlug,
        pluginKey: query.pluginKey,
        platform: query.platform,
        arch: query.arch,
        fileName: query.fileName,
      },
      onProgress,
    });
  },

  async createPlugin(payload: DesktopPluginCreateRequest): Promise<DesktopPluginItem> {
    const { data } = await apiClient.post('/desktop-plugins', payload);
    return data;
  },

  async updatePlugin(pluginId: string, payload: DesktopPluginUpdateRequest): Promise<DesktopPluginItem> {
    const { data } = await apiClient.patch(`/desktop-plugins/${pluginId}`, payload);
    return data;
  },

  async publishPlugin(pluginId: string): Promise<DesktopPluginItem> {
    const { data } = await apiClient.patch(`/desktop-plugins/${pluginId}/publish`);
    return data;
  },

  async unpublishPlugin(pluginId: string): Promise<DesktopPluginItem> {
    const { data } = await apiClient.patch(`/desktop-plugins/${pluginId}/unpublish`);
    return data;
  },

  async downloadUrl(payload: DesktopPluginDownloadUrlRequest): Promise<{
    pluginId: string;
    downloadUrl: string;
    expiresInSec: number;
  }> {
    const { data } = await apiClient.post('/desktop-plugins/download-url', payload);
    return data;
  },

  async deletePlugin(pluginId: string): Promise<void> {
    await apiClient.delete(`/desktop-plugins/${pluginId}`);
  },
};

export function useDesktopPlugins(filters?: DesktopPluginAdminListQuery) {
  return useQuery({
    queryKey: desktopPluginsQueryKeys.list(filters),
    queryFn: () => desktopPluginsApi.list(filters),
  });
}

export function usePublishDesktopPlugin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pluginId: string) => desktopPluginsApi.publishPlugin(pluginId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: desktopPluginsQueryKeys.all });
    },
  });
}

export function useUpdateDesktopPlugin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ pluginId, payload }: { pluginId: string; payload: DesktopPluginUpdateRequest }) =>
      desktopPluginsApi.updatePlugin(pluginId, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: desktopPluginsQueryKeys.all });
    },
  });
}

export function useDesktopPluginDownloadUrl() {
  return useMutation({
    mutationFn: (payload: DesktopPluginDownloadUrlRequest) => desktopPluginsApi.downloadUrl(payload),
  });
}

export function useDeleteDesktopPlugin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pluginId: string) => desktopPluginsApi.deletePlugin(pluginId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: desktopPluginsQueryKeys.all });
    },
  });
}
